import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Code, Search, Filter, ChevronRight, Check, X, 
  Bookmark, TrendingUp, Clock, ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProblemsListPage = () => {
  const { user } = useAuth();
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [selectedTag, setSelectedTag] = useState('');

  const tags = ['Arrays', 'Strings', 'Dynamic Programming', 'Trees', 'Graphs', 'Math', 'Sorting', 'Recursion'];

  useEffect(() => {
    fetchProblems();
  }, [page, difficulty, selectedTag]);

  const fetchProblems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (difficulty) params.append('difficulty', difficulty);
      if (selectedTag) params.append('tag', selectedTag);
      if (search) params.append('search', search);

      const res = await axios.get(`${API}/coding/problems?${params}`);
      setProblems(res.data.problems || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error('Failed to fetch problems:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchProblems();
  };

  const getDifficultyStyle = (diff) => {
    switch (diff) {
      case 'easy': return { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' };
      case 'medium': return { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' };
      case 'hard': return { color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200' };
      default: return { color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' };
    }
  };

  const getPoints = (diff) => {
    switch (diff) {
      case 'easy': return 10;
      case 'medium': return 25;
      case 'hard': return 50;
      default: return 0;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/coding" className="text-slate-400 hover:text-slate-600 transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Problem Set</h1>
              <p className="text-slate-600">Practice problems to sharpen your skills</p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="Search problems..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all"
                  data-testid="problem-search-input"
                />
              </div>
            </form>

            <div className="flex gap-3">
              <select
                value={difficulty}
                onChange={(e) => { setDifficulty(e.target.value); setPage(1); }}
                className="px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none bg-white min-w-[140px]"
                data-testid="difficulty-filter"
              >
                <option value="">All Levels</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>

              <select
                value={selectedTag}
                onChange={(e) => { setSelectedTag(e.target.value); setPage(1); }}
                className="px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none bg-white min-w-[160px]"
                data-testid="tag-filter"
              >
                <option value="">All Topics</option>
                {tags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Problems Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-sm font-medium text-slate-500">
            <div className="col-span-1 text-center">#</div>
            <div className="col-span-5">Problem</div>
            <div className="col-span-2 text-center">Difficulty</div>
            <div className="col-span-2 text-center">Points</div>
            <div className="col-span-2 text-center">Status</div>
          </div>

          {/* Problems List */}
          {loading ? (
            <div className="divide-y divide-slate-100">
              {[...Array(5)].map((_, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-4 px-6 py-5 animate-pulse">
                  <div className="col-span-1"><div className="h-4 bg-slate-200 rounded w-8 mx-auto"></div></div>
                  <div className="col-span-5"><div className="h-4 bg-slate-200 rounded w-3/4"></div></div>
                  <div className="col-span-2"><div className="h-6 bg-slate-200 rounded-full w-16 mx-auto"></div></div>
                  <div className="col-span-2"><div className="h-4 bg-slate-200 rounded w-12 mx-auto"></div></div>
                  <div className="col-span-2"><div className="h-6 bg-slate-200 rounded-full w-6 mx-auto"></div></div>
                </div>
              ))}
            </div>
          ) : problems.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {problems.map((problem, idx) => {
                const style = getDifficultyStyle(problem.difficulty);
                const solved = problem.solved || false;
                
                return (
                  <Link
                    key={problem.id}
                    to={`/coding/problem/${problem.id}`}
                    className="grid grid-cols-12 gap-4 px-6 py-5 hover:bg-slate-50 transition-colors group"
                    data-testid={`problem-row-${problem.id}`}
                  >
                    <div className="col-span-1 text-center text-slate-400 font-medium">
                      {(page - 1) * 20 + idx + 1}
                    </div>
                    <div className="col-span-5">
                      <div className="font-medium text-slate-900 group-hover:text-indigo-600 transition-colors mb-1">
                        {problem.title}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {problem.tags?.slice(0, 3).map((tag, tagIdx) => (
                          <span key={tagIdx} className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="col-span-2 flex items-center justify-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.color} border ${style.border}`}>
                        {problem.difficulty?.toUpperCase()}
                      </span>
                    </div>
                    <div className="col-span-2 text-center">
                      <span className="font-semibold text-indigo-600">{getPoints(problem.difficulty)}</span>
                      <span className="text-slate-400 text-sm"> pts</span>
                    </div>
                    <div className="col-span-2 flex items-center justify-center">
                      {solved ? (
                        <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center">
                          <Check className="text-emerald-600" size={16} />
                        </div>
                      ) : (
                        <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center">
                          <span className="text-slate-400 text-xs">—</span>
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="py-16 text-center">
              <Code className="mx-auto text-slate-300 mb-4" size={48} />
              <h3 className="font-semibold text-slate-700 mb-2">No Problems Found</h3>
              <p className="text-slate-500">Try adjusting your filters or search term</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <div className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProblemsListPage;
